import React, { createContext, useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import './Toast.css';
import { setToastSink } from './toastSink';

const ToastContext = createContext();

const AUTO_DISMISS_MS = { info: 4000, success: 4000, error: 7000, notify: 5000 };
const MAX_VISIBLE = 4;

// Ported from the retired ListenerConfirmation.jsx tag system — same domain vocabulary
// (Spanish/English), same colors, so toast.notify(msg, tagType) reads consistently with
// whatever the caller already had for `tagType`.
const TAG_CONFIG = [
    [/comentario|comment/i, { label: 'Comentario', color: '#9359FF' }],
    [/supervisor/i, { label: 'Supervisor', color: '#FF69B4' }],
    [/cliente|client/i, { label: 'Cliente', color: '#3b82f6' }],
    [/tarea|task/i, { label: 'Tarea', color: '#E9D92C' }],
    [/evento|event/i, { label: 'Evento', color: '#22c55e' }],
    [/documento|document/i, { label: 'Documento', color: '#DF2E5A' }],
    [/bloqueo|lock/i, { label: 'Bloqueo', color: '#ef4444' }],
    [/atraso|overdue/i, { label: 'Atraso', color: '#f59e0b' }],
    [/notificacion|notification/i, { label: 'Notificación', color: '#8b5cf6' }],
];

function resolveTag(tagType) {
    const match = tagType && TAG_CONFIG.find(([re]) => re.test(tagType));
    return match ? match[1] : { label: 'General', color: '#a3a3a3' };
}

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);
    const idRef = useRef(0);

    const dismiss = useCallback((id) => {
        setToasts((current) => current.filter((t) => t.id !== id));
    }, []);

    const push = useCallback((variant, message, tag, options = {}) => {
        const id = ++idRef.current;
        setToasts((current) => [...current.slice(-(MAX_VISIBLE - 1)), { id, variant, message, tag, options }]);
        if (variant !== 'prompt' && AUTO_DISMISS_MS[variant]) {
            setTimeout(() => dismiss(id), AUTO_DISMISS_MS[variant]);
        }
    }, [dismiss]);

    const toast = useMemo(() => ({
        error: (message) => push('error', message),
        success: (message) => push('success', message),
        info: (message) => push('info', message),
        notify: (message, tagType) => push('notify', message, resolveTag(tagType)),
        prompt: (message, options) => push('prompt', message, null, options),
    }), [push]);

    // Expose the toast API to the non-React call sites (module-level fetch wrappers in `app/`).
    useEffect(() => {
        setToastSink(toast);
        return () => setToastSink(null);
    }, [toast]);

    return (
        <ToastContext.Provider value={toast}>
            {children}
            {/* Portal a <body>: escapa el stacking context de #root para que los toasts queden
                por encima de overlays a pantalla completa y de otros portales (integración
                main→dev, epic A4 -- de main `3807652d`). */}
            {createPortal(
            <div className="cy-toast-container" role="status" aria-live="polite">
                {toasts.map((t) => (
                    <div
                        key={t.id}
                        className={`cy-toast cy-toast-${t.variant}`}
                        style={t.tag ? { borderLeftColor: t.tag.color } : undefined}
                    >
                        <span className="cy-toast-pulse" style={t.tag ? { background: t.tag.color } : undefined} />
                        {t.tag && <span className="cy-toast-tag" style={{ backgroundColor: t.tag.color }}>{t.tag.label}</span>}
                        <span className="cy-toast-message">{t.message}</span>
                        {t.variant === 'prompt' && t.options && (
                            <div className="cy-toast-actions" style={{ display: 'flex', gap: '8px', marginTop: '8px', width: '100%' }}>
                                <button onClick={() => { t.options.onConfirm?.(); dismiss(t.id); }} style={{ padding: '4px 12px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                                    {t.options.confirmText || 'Borrar'}
                                </button>
                                <button onClick={() => { t.options.onCancel?.(); dismiss(t.id); }} style={{ padding: '4px 12px', background: 'rgba(255,255,255,0.1)', color: '#ccc', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                                    {t.options.cancelText || 'Cancelar'}
                                </button>
                            </div>
                        )}
                        {t.variant !== 'prompt' && <button className="cy-toast-close" onClick={() => dismiss(t.id)} aria-label="Cerrar">×</button>}
                    </div>
                ))}
            </div>,
            document.body,
            )}
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};
